import isEqual from 'lodash-es/isEqual';
import { getInitialLanguage } from 'utils/languages';
import { LanguageContext } from 'context/LanguageProvider';
import { serializeOptions } from 'utils/api';
import { useContext, useState } from 'react';
import { useSafeContext } from 'react-safe-context-hooks';
import { WordPressContext } from 'context/WordPressProvider';
import isEmpty from 'lodash-es/isEmpty';
import { useEffect } from 'react';
import { get, set } from 'idb-keyval';

interface Params<T> {
  readonly endpoint?: string;
  readonly customUrl?: string;
  readonly cacheKey?: string;
  readonly slug?: string;
  readonly skip?: boolean;
  readonly embed?: boolean;
  readonly skipLang?: boolean;
  readonly preview?: string;
  readonly fields?: string;
  readonly options?: object;
}

interface StateProps<U> {
  data: U | undefined;
  error: null | any;
  loading: boolean;
  cached?: boolean;
  cacheKey?: string;
}

export const useApiRequest = <U, T extends object = object>(config: Params<T>) => {
  const DEBUG = false;
  const { endpoint = '', customUrl, fields, skip = false, cacheKey, slug, preview, embed = true, skipLang = false, options: baseOptions = {} } = config;
  const lang = getInitialLanguage();
  const { url, headers, mode } = useSafeContext(WordPressContext);
  const { language } = useContext(LanguageContext);
  const query = [customUrl || url, `/${endpoint}`];
  const requestLanguage = language || lang;
  const cachePhrase = `${cacheKey}${skipLang ? '' : `:${requestLanguage}`}`;
  const [shouldReturn, setShouldReturn] = useState(false);

  const [dataObject, setDataObject] = useState<StateProps<U>>({
    data: window?.globalCache?.items ? window.globalCache.items[cachePhrase] : undefined,
    error: false,
    loading: true,
    cached: false,
  });

  DEBUG && console.log('API - request body', { preview, cachePhrase, data: dataObject.data });

  const settings = {
    method: 'GET',
    headers: {
      ...headers,
      ...(!!preview && { 'X-WP-Nonce': preview }),
    },
    mode,
  };

  useEffect(() => {
    DEBUG && console.log('API - request global cache load', { cachePhrase, data: dataObject.data });

    if (!!cacheKey && window?.globalCache?.items && window?.globalCache?.items[cachePhrase]) {
      DEBUG && console.log('API - request ready to load from globalCache', { cachePhrase, data: dataObject.data });

      setDataObject({
        data: window?.globalCache?.items[cachePhrase],
        error: false,
        loading: false,
        cached: true,
      });
      setShouldReturn(true);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    DEBUG && console.log('API - request cache load', { cachePhrase, data: dataObject.data });

    if (!!cacheKey) {
      get(cachePhrase)
        .then(cachedData => {
          DEBUG && console.log('API - request ready to load from cache', { cachePhrase, data: dataObject.data });

          if (cachedData && !isEqual(window.globalCache.items[cachePhrase], cachedData)) {
            DEBUG && console.log('API - data from cache is different', { cachePhrase, data: dataObject.data });

            window.globalCache.items[cachePhrase] = cachedData;
            setDataObject({ data: cachedData, cached: true, cacheKey, loading: false, error: false });
          }
        })
        .catch(() => {
          console.info('IndexedDB not available');
        });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // @ts-ignore
    if (skip) {
      return;
    }

    DEBUG && console.log('API - request before fetch', { data: dataObject.data });

    const options = {
      ...baseOptions,
      ...(slug && { slug }),
      ...(fields && fields !== '' && { _fields: fields }),
      ...(embed && { _embed: 1 }),
      ...(!skipLang && { lang: requestLanguage }),
    };

    query.push(serializeOptions(options));
    const combinedQuery = query.join('');

    if (!dataObject.data) {
      setDataObject({ ...dataObject, loading: true });
    }

    fetch(combinedQuery, settings)
      .then(async response => {
        DEBUG && console.log('API - request after fetch', { cachePhrase, data: dataObject.data });

        const data = await response.json();
        if (!!cacheKey && !isEmpty(data)) {
          DEBUG && console.log('API - data exists', { cachePhrase, data: dataObject.data });

          get(cachePhrase)
            .then(currentData => {
              if (!currentData || !isEqual(currentData, data) || !isEqual(dataObject.data, data)) {
                DEBUG && console.log('API - data different - load from request', { cachePhrase, data: dataObject.data });

                window.globalCache.items[cachePhrase] = data;
                set(cachePhrase, data)
                  .then(() => {
                    setDataObject({
                      data,
                      error: !response.ok,
                      loading: false,
                      cacheKey,
                    });
                  })
                  .catch(() => {
                    console.info('IndexedDB not available');
                  });
              }
            })
            .catch(() => {
              console.info('IndexedDB not available');
              if (!isEqual(dataObject.data, data)) {
                DEBUG && console.log('API - data different - load from request', { cachePhrase, data: dataObject.data });
                setDataObject({
                  data,
                  error: !response.ok,
                  loading: false,
                  cacheKey,
                });
              }
            });
        } else {
          setDataObject({
            data,
            error: !response.ok,
            loading: false,
            cacheKey,
          });
        }
      })
      .catch(error => {
        //fetch throws an error only on network failure or if anything prevented the request from completing
        DEBUG && console.log('Maybe cors', error);
        setDataObject({
          data: undefined,
          error: { status: 'network_failure' },
          loading: false,
        });
      });
    // eslint-disable-next-line
  }, [cacheKey, skip, endpoint, customUrl, slug, JSON.stringify(baseOptions)]);

  return { ...dataObject, shouldReturn };
};
